
/**
 * CrisisEvent Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';
import { teamAssociationServiceMethods } from './team-association.mixin.js';

export class CrisisEventService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
        Object.assign(this, teamAssociationServiceMethods());
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                pagination: response.pagination || response.meta || {},
                message: response.message || 'CrisisEvent listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch crisis-event list');
        }
    }

    async getStats() {
        try {
            const response = await withRetry(() => this.api.getStats(), { maxRetries: 2 });
            const payload = response?.data !== undefined ? response.data : response;
            return {
                success: true,
                data: payload,
                message: response?.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch crisis-event stats');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch crisis-event');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'CrisisEvent oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create crisis-event');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'CrisisEvent güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update crisis-event');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'CrisisEvent silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete crisis-event');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async batchDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);

            return {
                success: true,
                deletedCount: response.deletedCount ?? response.data?.deletedCount ?? 0,
                message: response.message || response.data?.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to batch delete crisis-event items');
        }
    }

    // --- Activated BCPs Relation Methods ---

    async getRelatedBcps(id, params = {}) {
        try {
            const response = await this.api.getRelatedBcps(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'Related BCPs fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related BCPs');
        }
    }

    async addBcps(id, bcpIds) {
        try {
            const response = await this.api.addBcps(id, bcpIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'BCPs linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link BCPs');
        }
    }

    async removeBcp(id, bcpId) {
        try {
            const response = await this.api.removeBcp(id, bcpId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'BCP unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink BCP');
        }
    }

    async getRelatedBias(id, params = {}) {
        try {
            const response = await this.api.getRelatedBias(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                message: response.message || 'Related BIAs fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related BIAs');
        }
    }

    async addBias(id, biaIds) {
        try {
            const response = await this.api.addBias(id, biaIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'BIAs linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link BIAs');
        }
    }

    async removeBia(id, biaId) {
        try {
            const response = await this.api.removeBia(id, biaId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'BIA unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink BIA');
        }
    }

    // --- Processes Relation Methods ---

    async getRelatedProcesses(id, params = {}) {
        try {
            const response = await this.api.getRelatedProcesses(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'Related processes fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related processes');
        }
    }

    async addProcesses(id, processIds) {
        try {
            const response = await this.api.addProcesses(id, processIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Processes linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link processes');
        }
    }

    async removeProcess(id, processId) {
        try {
            const response = await this.api.removeProcess(id, processId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Process unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink process');
        }
    }

    // --- Assets Relation Methods ---

    async getRelatedAssets(id, params = {}) {
        try {
            const response = await this.api.getRelatedAssets(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'Related assets fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related assets');
        }
    }

    async addAssets(id, assetIds) {
        try {
            const response = await this.api.addAssets(id, assetIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Assets linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link assets');
        }
    }

    async removeAsset(id, assetId) {
        try {
            const response = await this.api.removeAsset(id, assetId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Asset unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink asset');
        }
    }

    // --- Vendors Relation Methods ---

    async getRelatedVendors(id, params = {}) {
        try {
            const response = await this.api.getRelatedVendors(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'Related vendors fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related vendors');
        }
    }

    async addVendors(id, vendorIds) {
        try {
            const response = await this.api.addVendors(id, vendorIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Vendors linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link vendors');
        }
    }

    async removeVendor(id, vendorId) {
        try {
            const response = await this.api.removeVendor(id, vendorId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Vendor unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink vendor');
        }
    }

    // --- Communications Relation Methods ---

    async getRelatedCommunications(id, params = {}) {
        try {
            const response = await this.api.getRelatedCommunications(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'Related communications fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related communications');
        }
    }

    async addCommunications(id, communicationIds) {
        try {
            const response = await this.api.addCommunications(id, communicationIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Communications linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link communications');
        }
    }

    async removeCommunication(id, communicationId) {
        try {
            const response = await this.api.removeCommunication(id, communicationId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Communication unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink communication');
        }
    }

    // --- Communication Plans Relation Methods ---

    async getRelatedCommunicationPlans(id, params = {}) {
        try {
            const response = await this.api.getRelatedCommunicationPlans(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'Related communication plans fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related communication plans');
        }
    }

    async addCommunicationPlans(id, planIds) {
        try {
            const response = await this.api.addCommunicationPlans(id, planIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Communication plans linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link communication plans');
        }
    }

    async removeCommunicationPlan(id, planId) {
        try {
            const response = await this.api.removeCommunicationPlan(id, planId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Communication plan unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink communication plan');
        }
    }

    // --- Recovery Resources Relation Methods ---

    async getRelatedRecoveryResources(id, params = {}) {
        try {
            const response = await this.api.getRelatedRecoveryResources(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {}
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related recovery resources');
        }
    }

    async addRecoveryResources(id, resourceIds) {
        try {
            const response = await this.api.addRecoveryResources(id, resourceIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Recovery resources linked'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link recovery resources');
        }
    }

    async removeRecoveryResource(id, resourceId) {
        try {
            const response = await this.api.removeRecoveryResource(id, resourceId);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink recovery resource');
        }
    }

    // --- Recovery Activities Relation Methods ---

    async getRelatedRecoveryActivities(id, params = {}) {
        try {
            const response = await this.api.getRelatedRecoveryActivities(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {}
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related recovery activities');
        }
    }

    async addRecoveryActivities(id, activityIds) {
        try {
            const response = await this.api.addRecoveryActivities(id, activityIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Recovery activities linked'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link recovery activities');
        }
    }

    async removeRecoveryActivity(id, activityId) {
        try {
            const response = await this.api.removeRecoveryActivity(id, activityId);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink recovery activity');
        }
    }

    async reorderRecoveryActivities(id, orderedActivityIds) {
        try {
            const response = await this.api.reorderRecoveryActivities(id, orderedActivityIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Recovery activities reordered'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to reorder recovery activities');
        }
    }

    // --- Lessons Learned Relation Methods ---

    async getRelatedLessonsLearned(id, params = {}) {
        try {
            const response = await this.api.getRelatedLessonsLearned(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {}
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related lessons learned');
        }
    }

    async addLessonsLearned(id, lessonIds) {
        try {
            const response = await this.api.addLessonsLearned(id, lessonIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Lessons linked'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link lessons');
        }
    }

    async removeLessonLearned(id, lessonId) {
        try {
            const response = await this.api.removeLessonLearned(id, lessonId);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink lesson');
        }
    }

    // --- Improvement Actions Relation Methods ---

    async getRelatedImprovementActions(id, params = {}) {
        try {
            const response = await this.api.getRelatedImprovementActions(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {}
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related improvement actions');
        }
    }

    async getLinkableImprovementActions(id, params = {}) {
        try {
            const response = await this.api.getLinkableImprovementActions(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {}
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch linkable improvement actions');
        }
    }

    async addImprovementActions(id, actionIds, extra = {}) {
        try {
            const response = await this.api.addImprovementActions(id, actionIds, extra);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Improvement actions linked'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link improvement actions');
        }
    }

    async unlinkImprovementActions(id, actionIds) {
        try {
            const response = await this.api.unlinkImprovementActions(id, actionIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Improvement actions unlinked'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink improvement actions');
        }
    }

    async removeImprovementAction(id, actionId) {
        try {
            const response = await this.api.removeImprovementAction(id, actionId);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink improvement action');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }

    async getPartyTemplate(params = {}) {
        try {
            const response = await this.api.getPartyTemplate(params);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to load party template');
        }
    }

    async getParty(id) {
        try {
            const response = await this.api.getParty(id);
            return {
                success: true,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch party instance');
        }
    }

    async syncParty(id, body) {
        try {
            const response = await this.api.syncParty(id, body);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to sync party');
        }
    }

    async activateBcp(id, bcpId) {
        try {
            const response = await this.api.activateBcp(id, bcpId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to activate BCP');
        }
    }
}
